import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnalyticsActivity } from 'src/domain/analytics';
import { FilterTabs } from 'src/components/FilterTabs/FilterTabs';
import { MetricCards } from './MetricCards';
import { AnalyticsCard } from './AnalyticsCard';
import { CalendarSummary } from './CalendarSummary';
import { MessageDirection, MessageTrend, messageTotal } from './MessageTrend';
import { ArrowUpRight } from 'lucide-react';
import { useAnalyticsSupportDrilldown } from './useAnalyticsSupportDrilldown';

const noStaffReplyPath = (month?: string) =>
    `/app/messages?filter=noStaffReply${
        month ? `&month=${month}` : ''
    }&from=analytics`;

export const MessageSummary = ({
    data,
    residentCount,
    timeFreed,
    selectedMonth,
    onSelectMonth,
    departmentFiltered = false,
}: {
    data: AnalyticsActivity;
    residentCount?: number;
    timeFreed: ReactNode;
    selectedMonth?: string;
    onSelectMonth: (month: string) => void;
    departmentFiltered?: boolean;
}) => {
    const { t, i18n } = useTranslation();
    const [direction, setDirection] = useState<MessageDirection>('all');
    const { messages, responses } = data.totals;
    const number = (value: number) =>
        value.toLocaleString(i18n.language, { maximumFractionDigits: 1 });
    const percent = (value: number, total: number) =>
        total ? `${number((value / total) * 100)}%` : '—';
    const originalMessages = messageTotal(messages, direction);
    const drilldown = useAnalyticsSupportDrilldown(data.organizations);
    const noStaffReply = responses.threads - responses.answered;
    const noStaffReplyTotal = (
        <span className="analytics-metric-total">
            /{number(responses.threads)}
        </span>
    );
    const openNoStaffReply = () =>
        drilldown.open({
            path: noStaffReplyPath(selectedMonth),
            noteKey: 'admin.analytics.openNoStaffReplyInSupport',
            counts: Object.fromEntries(
                data.organizations.map(org => [
                    org.id,
                    org.responses.threads - org.responses.answered,
                ]),
            ),
        });

    return (
        <>
            <div className="analytics-message-breakdown">
                <AnalyticsCard
                    title={t('admin.analytics.originalMessages')}
                    className="analytics-messages-card"
                    info={t('admin.analytics.trendNote')}
                    description={t('admin.analytics.trendShort')}
                    action={
                        <FilterTabs
                            label={t('admin.analytics.direction')}
                            value={direction}
                            onChange={setDirection}
                            options={(
                                ['all', 'organization', 'relative'] as const
                            ).map(value => ({
                                value,
                                label: t(`admin.analytics.direction_${value}`),
                            }))}
                        />
                    }
                >
                    <MetricCards
                        metrics={[
                            {
                                label: t('admin.analytics.originalMessages'),
                                value: number(originalMessages),
                                detail: t(
                                    'admin.analytics.messagesPerResident',
                                    {
                                        value: residentCount
                                            ? number(
                                                  originalMessages /
                                                      residentCount,
                                              )
                                            : '—',
                                    },
                                ),
                            },
                        ]}
                    />

                    <MessageTrend
                        trend={data.trend}
                        direction={direction}
                        month={selectedMonth ?? data.trendEndMonth}
                        isAllTime={!selectedMonth}
                        onSelectMonth={onSelectMonth}
                    />
                </AnalyticsCard>
                <div className="analytics-side-cards">
                    {timeFreed}
                    <AnalyticsCard
                        title={t('admin.analytics.comments')}
                        description={t('admin.analytics.allDirections')}
                    >
                        <MetricCards
                            metrics={[
                                {
                                    label: t(
                                        'admin.analytics.organizationReplies',
                                    ),
                                    value: number(messages.organizationReplies),
                                },
                                {
                                    label: t('admin.analytics.relativeReplies'),
                                    value: number(messages.relativeReplies),
                                },
                            ]}
                        />
                    </AnalyticsCard>
                    <AnalyticsCard
                        title={t('admin.analytics.audienceTitle')}
                        description={t('admin.analytics.audienceNote')}
                    >
                        <dl className="analytics-detail-list">
                            {(
                                [
                                    ['audienceAll', messages.allAudience],
                                    ['audienceGroup', messages.groupAudience],
                                    [
                                        'audienceIndividual',
                                        messages.individualAudience,
                                    ],
                                ] as const
                            ).map(([key, count]) => (
                                <div key={key}>
                                    <dt>{t(`admin.analytics.${key}`)}</dt>
                                    <dd>{number(count)}</dd>
                                </div>
                            ))}
                        </dl>
                    </AnalyticsCard>
                </div>
            </div>

            <div className="analytics-message-breakdown">
                <AnalyticsCard
                    title={t('admin.analytics.relativeConversations')}
                    className="analytics-responses"
                    info={t('admin.analytics.responseNote')}
                    description={t('admin.analytics.responseShort')}
                >
                    <MetricCards
                        metrics={[
                            {
                                label: t('admin.analytics.answered'),
                                value: (
                                    <>
                                        {number(responses.answered)}
                                        <span className="analytics-metric-total">
                                            /{number(responses.threads)}
                                        </span>
                                    </>
                                ),
                            },
                            {
                                label: t('admin.analytics.unanswered'),
                                value:
                                    drilldown.canOpen &&
                                    !departmentFiltered &&
                                    noStaffReply > 0 ? (
                                        <>
                                            <button
                                                type="button"
                                                className="analytics-drilldown-metric"
                                                onClick={openNoStaffReply}
                                                aria-label={t(
                                                    'admin.analytics.viewNoStaffReply',
                                                )}
                                                title={t(
                                                    'admin.analytics.viewNoStaffReply',
                                                )}
                                            >
                                                <span className="analytics-drilldown-metric-number">
                                                    {number(noStaffReply)}
                                                </span>
                                                <ArrowUpRight
                                                    size={18}
                                                    strokeWidth={2.25}
                                                    aria-hidden="true"
                                                />
                                            </button>
                                            {noStaffReplyTotal}
                                        </>
                                    ) : (
                                        <>
                                            {number(noStaffReply)}
                                            {noStaffReplyTotal}
                                        </>
                                    ),
                            },
                            {
                                label: t('admin.analytics.medianResponse'),
                                value:
                                    responses.medianHours === null
                                        ? '—'
                                        : t('admin.analytics.hours', {
                                              value: number(
                                                  responses.medianHours,
                                              ),
                                          }),
                            },
                            {
                                label: t('admin.analytics.replied24h'),
                                value: percent(
                                    responses.repliedWithin24h ?? 0,
                                    responses.answeredEligible24h ?? 0,
                                ),
                                detail:
                                    responses.answeredEligible24h === undefined
                                        ? undefined
                                        : t('admin.analytics.eligibleThreads', {
                                              count: responses.answeredEligible24h,
                                          }),
                            },
                            {
                                label: t('admin.analytics.replied48h'),
                                value: percent(
                                    responses.repliedWithin48h ?? 0,
                                    responses.answeredEligible48h ?? 0,
                                ),
                                detail:
                                    responses.answeredEligible48h === undefined
                                        ? undefined
                                        : t('admin.analytics.eligibleThreads', {
                                              count: responses.answeredEligible48h,
                                          }),
                            },
                            {
                                label: t('admin.analytics.replied72h'),
                                value: percent(
                                    responses.repliedWithin72h ?? 0,
                                    responses.answeredEligible72h ?? 0,
                                ),
                                detail:
                                    responses.answeredEligible72h === undefined
                                        ? undefined
                                        : t('admin.analytics.eligibleThreads', {
                                              count: responses.answeredEligible72h,
                                          }),
                            },
                        ]}
                    />
                </AnalyticsCard>
                <CalendarSummary calendar={data.totals.calendar} />
            </div>
            {drilldown.chooser}
        </>
    );
};
